import { datadogRum } from "@datadog/browser-rum"
import { MutationCache, QueryCache, QueryClient } from "@tanstack/react-query"
import { isAxiosError } from "axios"

import type { EnvironmentName } from "../models/environment.types"
import { HttpError, toHttpError } from "./api"
import { redirectToSignIn } from "./auth/auth"
import { showErrorNotification } from "./notification-bridge"

export const isUnauthorizedError = (error: unknown): boolean => {
  if (error instanceof HttpError) {
    return error.status === 401
  }

  if (isAxiosError(error)) {
    return error.response?.status === 401
  }

  return toHttpError(error).status === 401
}

type ThrowOnErrorPredicate<TError, TQuery> = (
  error: TError,
  query: TQuery
) => boolean

type ThrowOnErrorOverride<TError, TQuery> =
  | boolean
  | ThrowOnErrorPredicate<TError, TQuery>
  | undefined

export const resolveQueryThrowOnError = <TError = unknown, TQuery = unknown>(
  override?: ThrowOnErrorOverride<TError, TQuery>
): ThrowOnErrorPredicate<TError, TQuery> => {
  return (error, query) => {
    if (isUnauthorizedError(error)) {
      return false
    }

    if (typeof override === "function") {
      return override(error, query)
    }

    if (typeof override === "boolean") {
      return override
    }

    return true
  }
}

const shouldReportMutationErrorToDatadog = (
  error: unknown,
  handlesOwnErrors?: boolean
): boolean => {
  if (!handlesOwnErrors) {
    return true
  }

  const status = toHttpError(error).status
  const isExpectedClientError = status >= 400 && status < 500

  return !isExpectedClientError
}

const maxRetries =
  process.env.REACT_APP_ENV === ("acceptance" satisfies EnvironmentName) ||
  process.env.REACT_APP_ENV === ("development" satisfies EnvironmentName) ||
  process.env.REACT_APP_ENV === ("fed" satisfies EnvironmentName) ||
  process.env.REACT_APP_ENV === ("production" satisfies EnvironmentName)
    ? 3
    : 1

export const queryClient = new QueryClient({
  queryCache: new QueryCache({
    onError: (error, query) => {
      if (isUnauthorizedError(error)) {
        if (!query.meta?.skipAuthRedirect) {
          redirectToSignIn()
        }
        return
      }

      datadogRum.addError(error)
    },
  }),
  mutationCache: new MutationCache({
    onError: (error, _variables, _context, mutation) => {
      if (isUnauthorizedError(error)) {
        redirectToSignIn()
        return
      }

      if (
        shouldReportMutationErrorToDatadog(
          error,
          mutation.meta?.handlesOwnErrors
        )
      ) {
        datadogRum.addError(error)
      }

      if (mutation.meta?.handlesOwnErrors) {
        return
      }

      showErrorNotification(error)
    },
  }),
  defaultOptions: {
    queries: {
      retry: (failureCount, error) => {
        if (isUnauthorizedError(error)) {
          return false
        }

        return failureCount < maxRetries
      },
      gcTime: 1000 * 60 * 5, // milliseconds * seconds * minutes
      staleTime: 1000 * 60 * 5, // milliseconds * seconds * minutes
    },
  },
})
